import type { ApiResponse, TeamNames, TeamColors } from "@ihs-widget/api/interfaces";
import { processMatchClockData } from "@ihs-widget/api/commonAPI";

let currentIndexOverview = 1;
let currentIndexStatsConsole = 1

export const getFixturesOverviewDataById = async (eventId: string | null) => { 
    try {
        
        if (eventId?.includes('_')){
            const eventIdWithoutIndex = eventId.split('_')[0];
            const result = await import(`./mockData/StatsConsole/overviewData/${eventIdWithoutIndex}/${eventId}.json`);
            return { data: result, error: null };
        } else {
            const lengthData = await import(`./mockData/StatsConsole/overviewData/${eventId}/example_length.json`);
            const filesQuantity = lengthData.length;
            const result = await import(`./mockData/StatsConsole/overviewData/${eventId}/${eventId}_${currentIndexOverview}.json`);
            currentIndexOverview = currentIndexOverview % filesQuantity + 1; 
            return { data: result, error: null };
        }
    } catch (error) {
        console.error(`Not found: ${eventId}`);
        return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
}

export const getFixturesStatsConsoleData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        if (!eventId) {
            return { data: null, error: new Error('EventId is required') };
        }
        
        // Check if eventId is a number or hash
        const isNumeric = eventId && !isNaN(parseInt(eventId)) && isFinite(parseInt(eventId));
        
        // Special handling for numeric eventIds with subfolders (105, 106, 107, 108)
        if (isNumeric && ['105', '106', '107', '108'].some(prefix => eventId.startsWith(prefix))) {
            try {
                const lengthData = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1; 
                return { data: result.default?.statistics ?? [], error: null };
            } catch (subfolderError) {
                // Fallback to direct file if subfolder doesn't exist
                try {
                    const result = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}.json`);
                    return { data: result.default?.statistics ?? [], error: null };
                } catch (directError) {
                    throw subfolderError;
                }
            }
        }

        // For numeric eventId with special prefixes
        if (isNumeric && (eventId.startsWith('0') || eventId.startsWith('1') || eventId.startsWith('2') || eventId.startsWith('98') || eventId.startsWith('23') || eventId.startsWith('24'))) {
            try {
                const result = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}.json`);
                return { data: result.default?.statistics ?? [], error: null };
            } catch (directError) {
                // Check subfolder
                try {
                    const lengthData = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}/example_length.json`);
                    const filesQuantity = parseInt(lengthData.length);
                    const result = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                    currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                    return { data: result.default?.statistics ?? [], error: null };
                } catch (subfolderError) {
                    throw directError;
                }
            }
        }

        // 1. Try to load directly (for hash eventId or other numeric)
        try {
            const result = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}.json`);
            return { data: result.default?.statistics ?? [], error: null };
        } catch (directError) {
            // 2. Check if subfolder with example_length.json exists
            try {
                const lengthData = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/statsConsoleData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                return { data: result.default?.statistics ?? [], error: null };
            } catch (subfolderError) {
                throw directError; // Re-throw original error
            }
        }
    } catch (error) {
        return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
}

export const getFixturesStatsTabData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        if (eventId === null) {
            const statsData = await import(`./mockData/StatsConsole/statsTabData/0.json`);
            return { data: statsData, error: null };
        }
        
        // Check if eventId is a number or hash
        const isNumeric = eventId && !isNaN(parseInt(eventId)) && isFinite(parseInt(eventId));
        
        // Special handling for numeric eventIds with subfolders (213, 214, 215, 216)
        if (isNumeric && ['213', '214', '215', '216'].some(prefix => eventId.startsWith(prefix))) {
            try {
                const lengthData = await import(`./mockData/StatsConsole/statsTabData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/statsTabData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1; 
                return { data: result.default, error: null };
            } catch (subfolderError) {
                // Fallback to direct file if subfolder doesn't exist
                try {
                    const statsData = await import(`./mockData/StatsConsole/statsTabData/${eventId}.json`);
                    return { data: statsData, error: null };
                } catch (directError) {
                    throw subfolderError;
                }
            }
        }
        
        // For numeric eventId with special handling
        if (isNumeric) {
            if (parseInt(eventId) === 98) {
                try {
                    const statsData = await import(`./mockData/StatsConsole/statsTabData/98.json`);
                    return { data: statsData, error: null };
                } catch (directError) {
                    // Check subfolder
                    try {
                        const lengthData = await import(`./mockData/StatsConsole/statsTabData/98/example_length.json`);
                        const filesQuantity = parseInt(lengthData.length);
                        const result = await import(`./mockData/StatsConsole/statsTabData/98/98_${currentIndexStatsConsole}.json`);
                        currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                        return { data: result.default, error: null };
                    } catch (subfolderError) {
                        throw directError;
                    }
                }
            }
            if (parseInt(eventId) >= 200) {
                try {
                    const statsData = await import(`./mockData/StatsConsole/statsTabData/${eventId}.json`);
                    return { data: statsData, error: null };
                } catch (directError) {
                    // Check subfolder
                    try {
                        const lengthData = await import(`./mockData/StatsConsole/statsTabData/${eventId}/example_length.json`);
                        const filesQuantity = parseInt(lengthData.length);
                        const result = await import(`./mockData/StatsConsole/statsTabData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                        currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                        return { data: result.default, error: null };
                    } catch (subfolderError) {
                        throw directError;
                    }
                }
            }
            if (parseInt(eventId) > 2 && parseInt(eventId) !== 98) {
                const statsData = await import(`./mockData/StatsConsole/statsTabData/0.json`);
                return { data: statsData, error: null };
            }
        }
        
        // 1. Try to load directly (for hash eventId or numeric <= 2)
        try {
            const statsData = await import(`./mockData/StatsConsole/statsTabData/${eventId}.json`);
            return { data: statsData, error: null };
        } catch (directError) {
            // 2. Check if subfolder with example_length.json exists
            try {
                const lengthData = await import(`./mockData/StatsConsole/statsTabData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/statsTabData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                return { data: result.default, error: null };
            } catch (subfolderError) {
                throw directError; // Re-throw original error
            }
        }
    } catch (error) {
        console.error('Error fetching stats tab data:', error);
        return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
}

const PLAYERS_TEST_EVENT_IDS = [
    'playersTestFormat',
    'playersTestOwnGoal',
    'playersTestAllGoalTypes',
    'playersTestSamePlayerOwnRegular',
    'playersTestOneTeamZero',
    'playersTestNoData',
] as const;

export const getFixturesPlayersTabData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        if (eventId === null) {
            const playersData = await import(`./mockData/StatsConsole/playersTabData/0.json`);
            return { data: playersData, error: null };
        }

        if (PLAYERS_TEST_EVENT_IDS.includes(eventId as typeof PLAYERS_TEST_EVENT_IDS[number])) {
            const playersData = await import(`./mockData/StatsConsole/playersTabData/playersTestMockData/${eventId}.json`);
            return { data: playersData?.default ?? playersData, error: null };
        }
        
        // Check if eventId is a number or hash
        const isNumeric = eventId && !isNaN(parseInt(eventId)) && isFinite(parseInt(eventId));
        
        // For numeric eventId with special handling
        if (isNumeric) {
            if (parseInt(eventId) === 98) {
                try {
                    const playersData = await import(`./mockData/StatsConsole/playersTabData/98.json`);
                    return { data: playersData, error: null };
                } catch (directError) {
                    // Check subfolder
                    try {
                        const lengthData = await import(`./mockData/StatsConsole/playersTabData/98/example_length.json`);
                        const filesQuantity = parseInt(lengthData.length);
                        const result = await import(`./mockData/StatsConsole/playersTabData/98/98_${currentIndexStatsConsole}.json`);
                        currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                        return { data: result, error: null };
                    } catch (subfolderError) {
                        throw directError;
                    }
                }
            }
            if (parseInt(eventId) === 99) {
                try {
                    const playersData = await import(`./mockData/StatsConsole/playersTabData/99.json`);
                    return { data: playersData, error: null };
                } catch (directError) {
                    // Check subfolder
                    try {
                        const lengthData = await import(`./mockData/StatsConsole/playersTabData/99/example_length.json`);
                        const filesQuantity = parseInt(lengthData.length);
                        const result = await import(`./mockData/StatsConsole/playersTabData/99/99_${currentIndexStatsConsole}.json`);
                        currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                        return { data: result, error: null };
                    } catch (subfolderError) {
                        throw directError;
                    }
                }
            }
            if (parseInt(eventId) > 230) {
                try {
                    const playersData = await import(`./mockData/StatsConsole/playersTabData/${eventId}.json`);
                    return { data: playersData, error: null };
                } catch (directError) {
                    // Check subfolder
                    try {
                        const lengthData = await import(`./mockData/StatsConsole/playersTabData/${eventId}/example_length.json`);
                        const filesQuantity = parseInt(lengthData.length);
                        const result = await import(`./mockData/StatsConsole/playersTabData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                        currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                        return { data: result, error: null };
                    } catch (subfolderError) {
                        throw directError;
                    }
                }
            }
            if (parseInt(eventId) > 3) {
                const playersData = await import(`./mockData/StatsConsole/playersTabData/0.json`);
                return { data: playersData, error: null };
            }
        }
        
        // 1. Try to load directly (for hash eventId or numeric <= 3)
        try {
            const playersData = await import(`./mockData/StatsConsole/playersTabData/${eventId}.json`);
            return { data: playersData, error: null };
        } catch (directError) {
            // 2. Check if subfolder with example_length.json exists
            try {
                const lengthData = await import(`./mockData/StatsConsole/playersTabData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/playersTabData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                return { data: result, error: null };
            } catch (subfolderError) {
                throw directError; // Re-throw original error
            }
        }
    } catch (error) {
        console.error('Error fetching players tab data:', error);
        return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
}

export const getFixturesH2HData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        if (eventId === null) {
            const h2hData = await import(`./mockData/StatsConsole/h2hData/0.json`);
            return { data: h2hData, error: null };
        }
        
        // Check if eventId is a number or hash
        const isNumeric = eventId && !isNaN(parseInt(eventId)) && isFinite(parseInt(eventId));
        
        if (isNumeric && parseInt(eventId) > 250) {
            const h2hData = await import(`./mockData/StatsConsole/h2hData/0.json`);
            return { data: h2hData, error: null };
        }
        
        // 1. Try to load directly
        try {
            const h2hData = await import(`./mockData/StatsConsole/h2hData/${eventId}.json`);
            return { data: h2hData, error: null };
        } catch (directError) {
            // 2. Check if subfolder with example_length.json exists
            try {
                const lengthData = await import(`./mockData/StatsConsole/h2hData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/h2hData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                return { data: result, error: null };
            } catch (subfolderError) {
                throw directError; // Re-throw original error
            }
        }
    } catch (error) {
        console.error('Error fetching h2h data:', error);
        return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
}

export const getFixturesLineupData = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        if (eventId === null) {
            const lineupData = await import(`./mockData/StatsConsole/lineupData/0.json`);
            return { data: lineupData, error: null };
        }
        
        // Check if eventId is a number or hash
        const isNumeric = eventId && !isNaN(parseInt(eventId)) && isFinite(parseInt(eventId));
        
        // For numeric eventId with special handling
        if (isNumeric) {
            if (parseInt(eventId) > 220) {
                try {
                    const lineupData = await import(`./mockData/StatsConsole/lineupData/${eventId}.json`);
                    return { data: lineupData, error: null };
                } catch (directError) {
                    // Check subfolder
                    try {
                        const lengthData = await import(`./mockData/StatsConsole/lineupData/${eventId}/example_length.json`);
                        const filesQuantity = parseInt(lengthData.length);
                        const result = await import(`./mockData/StatsConsole/lineupData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                        currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                        return { data: result, error: null };
                    } catch (subfolderError) {
                        throw directError;
                    }
                }
            }
            if (parseInt(eventId) > 15) {
                const lineupData = await import(`./mockData/StatsConsole/lineupData/0.json`);
                return { data: lineupData, error: null };
            }
        }
        
        // 1. Try to load directly (for hash eventId or numeric <= 15)
        try {
            const lineupData = await import(`./mockData/StatsConsole/lineupData/${eventId}.json`);
            return { data: lineupData, error: null };
        } catch (directError) {
            // Fallback for Players Tab test event IDs (no dedicated lineup mock)
            if (PLAYERS_TEST_EVENT_IDS.includes(eventId as typeof PLAYERS_TEST_EVENT_IDS[number])) {
                const lineupData = await import(`./mockData/StatsConsole/lineupData/0.json`);
                return { data: lineupData, error: null };
            }
            // 2. Check if subfolder with example_length.json exists
            try {
                const lengthData = await import(`./mockData/StatsConsole/lineupData/${eventId}/example_length.json`);
                const filesQuantity = parseInt(lengthData.length);
                const result = await import(`./mockData/StatsConsole/lineupData/${eventId}/${eventId}_${currentIndexStatsConsole}.json`);
                currentIndexStatsConsole = currentIndexStatsConsole % filesQuantity + 1;
                return { data: result, error: null };
            } catch (subfolderError) {
                throw directError; // Re-throw original error
            }
        }
    } catch (error) {
        console.error('Error fetching lineup data:', error);
        return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
}


export const getFixturesTeamNames = async (eventId: string | null): Promise<ApiResponse<TeamNames>> => {
    try {
        const defaultTeamNames = {
            home_team: 'Home Team',
            away_team: 'Away Team'
        };
        
        if (!eventId) {
            return { data: defaultTeamNames, error: null };
        }
        
        // Try to load team names from mockData file
        try {
            const teamNamesData = await import(`./mockData/StatsConsole/teamNamesData/${eventId}.json`);
            return { data: teamNamesData, error: null };
        } catch (importError) {
            // If file doesn't exist, fallback to default values
            return { data: defaultTeamNames, error: null };
        }
    } catch (error) {
        console.error('Error fetching fixtures team names:', error);
        return { 
            data: { home_team: 'Home Team', away_team: 'Away Team' }, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}

export const getFixturesTeamColors = async (eventId: string | null): Promise<ApiResponse<TeamColors>> => {
    try {
        const defaultColors = {
            homeColor: '#147B45',
            awayColor: '#333333'
        };
        
        if (!eventId) {
            return { data: defaultColors, error: null };
        }
        
        // Try to load team colors from mockData file
        try {
            const teamColorsData = await import(`./mockData/StatsConsole/teamColorsData/${eventId}.json`);
            // Transform API format {home: {color}, away: {color}} to {homeColor, awayColor}
            if (teamColorsData?.home?.color && teamColorsData?.away?.color) {
                return {
                    data: {
                        homeColor: teamColorsData.home.color,
                        awayColor: teamColorsData.away.color
                    },
                    error: null
                };
            }
            // If structure is already correct, return as is
            if (teamColorsData?.homeColor && teamColorsData?.awayColor) {
                return { data: teamColorsData, error: null };
            }
            // Fallback to default if structure is unexpected
            return { data: defaultColors, error: null };
        } catch (importError) {
            // If file doesn't exist, fallback to default colors
            return { data: defaultColors, error: null };
        }
    } catch (error) {
        console.error('Error fetching fixtures team colors:', error);
        return { 
            data: { homeColor: '#147B45', awayColor: '#333333' }, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
}

export const getOverviewFixturesDataFromOurBackend = async (eventId: string | null): Promise<ApiResponse<any>> => {
    try {
        const overviewResponse = await getFixturesOverviewDataById(eventId);
        
        if (overviewResponse.error) {
            return overviewResponse;
        }
        
        if (overviewResponse.data?.match_clock) {
            const processedMatchClock = processMatchClockData(overviewResponse.data.match_clock);
            overviewResponse.data = {
                ...overviewResponse.data,
                match_clock: processedMatchClock
            };
        }
        return overviewResponse;
        
    } catch (error) {
        console.error('Error fetching fixtures overview data from our backend:', error);
        return { 
            data: null, 
            error: error instanceof Error ? error : new Error('Unknown error') 
        };
    }
};